import React, { useState, useEffect, useRef } from 'react';
import { useSeasonalTheme } from '../../contexts/SeasonalThemeContext';
import { SEASONAL_THEMES } from '../../config/seasonalThemes';

// Floating Ghost Component
export const FloatingGhost = ({ delay = 0, duration = 15 }) => {
  const [position] = useState({
    startX: Math.random() * 100,
    endX: Math.random() * 100,
    y: Math.random() * 100,
  });

  return (
    <div
      className="floating-ghost"
      style={{
        position: 'fixed',
        left: `${position.startX}%`,
        top: `${position.y}%`,
        fontSize: '2rem',
        opacity: 0.3,
        zIndex: 1,
        pointerEvents: 'none',
        animation: `floatGhost ${duration}s ease-in-out ${delay}s infinite`,
      }}
    >
      👻
    </div>
  );
};

// Multiple Floating Ghosts
export const HalloweenGhosts = ({ count = 5 }) => {
  return (
    <>
      {[...Array(count)].map((_, i) => (
        <FloatingGhost key={i} delay={i * 3} duration={15 + Math.random() * 10} />
      ))}
      <style>{`
        @keyframes floatGhost {
          0% {
            transform: translate(0, 0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.3;
          }
          50% {
            transform: translate(${Math.random() * 100 - 50}px, ${Math.random() * 100 - 50}px) rotate(${Math.random() * 20 - 10}deg);
            opacity: 0.4;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translate(${Math.random() * 200 - 100}px, ${Math.random() * 200 - 100}px) rotate(${Math.random() * 40 - 20}deg);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};

// Bat Flying Animation
export const FlyingBat = ({ delay = 0 }) => {
  return (
    <div
      className="flying-bat"
      style={{
        position: 'fixed',
        top: `${Math.random() * 30}%`,
        right: '-50px',
        fontSize: '1.5rem',
        opacity: 0.4,
        zIndex: 1,
        pointerEvents: 'none',
        animation: `flyBat ${8 + Math.random() * 4}s linear ${delay}s infinite`,
      }}
    >
      🦇
    </div>
  );
};

export const HalloweenBats = ({ count = 3 }) => {
  return (
    <>
      {[...Array(count)].map((_, i) => (
        <FlyingBat key={i} delay={i * 5} />
      ))}
      <style>{`
        @keyframes flyBat {
          0% {
            transform: translateX(0) translateY(0) rotate(0deg);
          }
          25% {
            transform: translateX(-25vw) translateY(-20px) rotate(-5deg);
          }
          50% {
            transform: translateX(-50vw) translateY(10px) rotate(5deg);
          }
          75% {
            transform: translateX(-75vw) translateY(-15px) rotate(-3deg);
          }
          100% {
            transform: translateX(-100vw) translateY(0) rotate(0deg);
          }
        }
      `}</style>
    </>
  );
};

// Spider Web Decoration
export const SpiderWeb = ({ position = 'top-left' }) => {
  const positionStyles = {
    'top-left': { top: 0, left: 0 },
    'top-right': { top: 0, right: 0 },
    'bottom-left': { bottom: 0, left: 0 },
    'bottom-right': { bottom: 0, right: 0 },
  };

  return (
    <div
      style={{
        position: 'absolute',
        ...positionStyles[position],
        fontSize: '3rem',
        opacity: 0.15,
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      🕸️
    </div>
  );
};

// Pumpkin Decorations
export const PumpkinDecoration = ({ position = 'left', animated = true }) => {
  const [isGlowing, setIsGlowing] = useState(false);

  useEffect(() => {
    if (animated) {
      const interval = setInterval(() => {
        setIsGlowing(prev => !prev);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [animated]);

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        fontSize: '1.5rem',
        opacity: 0.7,
        transition: 'all 0.5s ease',
        filter: isGlowing ? 'drop-shadow(0 0 8px #ff6b35)' : 'none',
        transform: isGlowing ? 'scale(1.1)' : 'scale(1)',
      }}
    >
      🎃
    </div>
  );
};

// Halloween Cursor Trail
export const HalloweenCursorTrail = () => {
  const [trails, setTrails] = useState([]);

  useEffect(() => {
    let trailId = 0;

    const handleMouseMove = (e) => {
      const emoji = ['🎃', '👻', '🦇', '🕷️'][Math.floor(Math.random() * 4)];
      const newTrail = {
        id: trailId++,
        x: e.clientX,
        y: e.clientY,
        emoji,
      };

      setTrails(prev => [...prev.slice(-5), newTrail]);
    };

    // Only add trail occasionally to avoid performance issues
    let lastTrailTime = 0;
    const throttledMouseMove = (e) => {
      const now = Date.now();
      if (now - lastTrailTime > 200) {
        handleMouseMove(e);
        lastTrailTime = now;
      }
    };

    window.addEventListener('mousemove', throttledMouseMove);

    return () => {
      window.removeEventListener('mousemove', throttledMouseMove);
    };
  }, []);

  useEffect(() => {
    if (trails.length > 0) {
      const timer = setTimeout(() => {
        setTrails(prev => prev.slice(1));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [trails]);

  return (
    <>
      {trails.map((trail, index) => (
        <div
          key={trail.id}
          style={{
            position: 'fixed',
            left: trail.x,
            top: trail.y,
            pointerEvents: 'none',
            fontSize: '1rem',
            opacity: 0.6 - (index * 0.1),
            zIndex: 9999,
            animation: 'fadeOutTrail 1s ease-out forwards',
          }}
        >
          {trail.emoji}
        </div>
      ))}
      <style>{`
        @keyframes fadeOutTrail {
          0% {
            opacity: 0.6;
            transform: scale(1) translateY(0);
          }
          100% {
            opacity: 0;
            transform: scale(0.5) translateY(-20px);
          }
        }
      `}</style>
    </>
  );
};

// Halloween Header Decoration - Clickable dropdown menu
export const HalloweenHeader = () => {
  const { activeTheme, themeEnabled, toggleTheme } = useSeasonalTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const badges = {
    halloween: '🎃',
    christmas: '🎄',
    melbourneCup: '🏇',
    newYear: '🎉',
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  if (!activeTheme) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          animation: themeEnabled ? 'gentleBounce 3s ease-in-out infinite' : 'none',
        }}
        className="hover:bg-gray-100 dark:hover:bg-gray-700"
        title="Click to open Halloween theme menu"
      >
        <PumpkinDecoration animated={themeEnabled} />
        <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>
          Happy Halloween! 👻
        </span>
        <style>{`
          @keyframes gentleBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }
        `}</style>
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div
          className="absolute top-full mt-2 right-0 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
          style={{ maxHeight: '400px', overflowY: 'auto' }}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white">
            <h3 className="font-bold text-sm">Seasonal Themes</h3>
            <p className="text-xs opacity-90 mt-0.5">Festive decorations throughout the year</p>
          </div>

          {/* Active Theme Section */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Active Theme</span>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
              <div className="mb-3">
                <h4 className="font-bold text-base text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
                  <span className="text-xl">{badges[activeTheme.id]}</span>
                  {activeTheme.name}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {activeTheme.id === 'halloween' && 'Ghosts, bats, and spooky decorations'}
                  {activeTheme.id === 'christmas' && 'Snowfall and festive cheer'}
                  {activeTheme.id === 'melbourneCup' && 'The race that stops a nation'}
                  {activeTheme.id === 'newYear' && 'Fireworks and celebrations'}
                </p>
              </div>

              {/* Emoji Effects */}
              <div className="flex flex-wrap gap-2">
                {activeTheme.animations.emojiEffects.map((emoji, i) => (
                  <span key={i} className="text-2xl">{emoji}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Coming Soon Themes */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">Coming Soon</span>
            <div className="space-y-2">
              {Object.values(SEASONAL_THEMES)
                .filter(theme => theme.id !== activeTheme.id)
                .map(theme => (
                  <div key={theme.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{badges[theme.id] || '🎊'}</span>
                      <h5 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{theme.name}</h5>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {theme.id === 'christmas' && 'Snowfall and festive cheer'}
                      {theme.id === 'melbourneCup' && 'The race that stops a nation'}
                      {theme.id === 'newYear' && 'Fireworks and celebrations'}
                    </p>
                  </div>
                ))}
            </div>
          </div>

          {/* Toggle Button */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Decorations Enabled</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold ${themeEnabled ? 'text-orange-600' : 'text-gray-400'}`}>
                  {themeEnabled ? 'ON' : 'OFF'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTheme();
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    themeEnabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      themeEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Click to {themeEnabled ? 'disable' : 'enable'} seasonal animations
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Complete Halloween Theme Component
export const HalloweenTheme = ({ showGhosts = true, showBats = true, showCursorTrail = false }) => {
  return (
    <>
      {showGhosts && <HalloweenGhosts count={5} />}
      {showBats && <HalloweenBats count={3} />}
      {showCursorTrail && <HalloweenCursorTrail />}
    </>
  );
};
